// Turns the employee-import problems the server reports (failed rows and
// warnings, each with a stable `code`) into plain-language explanations:
// what is wrong, the cell at fault, what it clashes with, and how to fix it.
// Shared by the preview (before commit) and the results (after commit).

// Excel column -> Arabic label. Unknown columns show as written in the file.
const FIELD_LABELS_AR = {
  "Client Code": "كود الموظف",
  "Client Name": "اسم العميل",
  "Email": "البريد الإلكتروني",
  "Phone number": "رقم الموبايل",
  "National ID": "الرقم القومي",
  "DOB": "تاريخ الميلاد",
  "Age": "السن",
  "Hiring Date": "تاريخ التعيين",
  "Resigned Date": "تاريخ الاستقالة",
  "Be-Regular Date": "تاريخ التثبيت",
  "To Be Regular Or Not": "مثبّت أم لا",
  "working age(year)": "مدة العمل (سنوات)",
  "working age(month)": "مدة العمل (شهور)",
  "Contract Date": "تاريخ العقد",
  "Contract Expiration": "انتهاء العقد",
  "Term of contract": "مدة العقد",
  "Social Insurance Start Date": "تاريخ بدء التأمينات",
  "months of Social Insurance": "شهور التأمينات",
  "Health Certificate Expiry Date": "انتهاء الشهادة الصحية",
  "Driving License Expiry date": "انتهاء رخصة القيادة",
  "Date of last contract": "تاريخ آخر عقد",
  "Expiration of last contract": "انتهاء آخر عقد",
  "Graduation year": "سنة التخرج",
  "Insurance Salary": "الأجر التأميني",
  "Gross Salary": "إجمالي الراتب",
  "Net Salary": "صافي الراتب",
  "Variable Salary": "الراتب المتغير",
  "variable salary": "الراتب المتغير",
  "transportation allowance": "بدل الانتقالات",
  "Account": "حساب الدخول",
  "Email or Phone number": "البريد الإلكتروني أو رقم الموبايل",
};

// Field keys used by the duplicate-review diff -> Excel column names.
const DIFF_FIELD_COLUMNS = {
  sr: "English Name", clientCode: "Client Code", arabicName: "Arabic Name", employeeType: "Position",
  location: "Location", nationalId: "National ID", dob: "DOB", age: "Age", gender: "Gender",
  phoneNumber: "Phone number", emergencyPhoneNumber: "Emergency Phone Number", hiringDate: "Hiring Date",
  resignedDate: "Resigned Date", resignReasons: "Resign Reasons", beRegularDate: "Be-Regular Date",
  toBeRegular: "To Be Regular Or Not", workingAgeYears: "working age(year)", workingAgeMonths: "working age(month)",
  laborMode: "Labor Mode", contractCompany: "Contract Company", contractDate: "Contract Date",
  termOfContract: "Term of contract", contractExpiration: "Contract Expiration",
  insuredBeginningDate: "Social Insurance Start Date", insuranceStatus: "Social Insurance Status",
  monthsForInsurance: "months of Social Insurance", insuranceNumber: "Insurance Number",
  insuranceSalary: "Insurance Salary", email: "Email", address: "Address", arabicAddress: "Arabic address",
  city: "City", country: "Country", religion: "Religion", maritalStatus: "Marital Status",
  nationality: "Nationality", graduateInstitution: "Graduate Institution", major: "Major",
  graduationYear: "Graduation year", militaryStatus: "Military Status",
  drivingLicenseDegree: "Driving License Degree", drivingLicenseExpiry: "Driving License Expiry date",
  lastContractDate: "Date of last contract", lastContractTerm: "Last Contract Term",
  lastContractExpiration: "Expiration of last contract", medicalInsuranceStage: "Medical Insurance Stage",
  medicalNumber: "Medical Number", netSalary: "Net Salary", grossSalary: "Gross Salary",
  employeeState: "Employee state", idRegion: "ID Region", accountNumber: "Account Number", bankName: "Bank Name",
  transportationAllowances: "transportation allowance", variableSalary: "Variable Salary",
  workingCompany: "Working Company",
};

const EXTRA_AR = {
  "English Name": "الاسم بالإنجليزية", "Arabic Name": "الاسم بالعربية", "Position": "الوظيفة",
  "Location": "الموقع", "Gender": "النوع", "Emergency Phone Number": "رقم الطوارئ",
  "Resign Reasons": "سبب الاستقالة", "Labor Mode": "نوع التعاقد", "Contract Company": "شركة التعاقد",
  "Social Insurance Status": "حالة التأمينات", "Insurance Number": "الرقم التأميني", "Address": "العنوان",
  "Arabic address": "العنوان بالعربية", "City": "المدينة", "Country": "الدولة", "Religion": "الديانة",
  "Marital Status": "الحالة الاجتماعية", "Nationality": "الجنسية", "Graduate Institution": "جهة التخرج",
  "Major": "التخصص", "Military Status": "الموقف من التجنيد", "Driving License Degree": "درجة الرخصة",
  "Last Contract Term": "نوع آخر عقد", "Medical Insurance Stage": "مرحلة التأمين الطبي",
  "Medical Number": "الرقم الطبي", "Employee state": "حالة الموظف", "ID Region": "جهة إصدار البطاقة",
  "Account Number": "رقم الحساب البنكي", "Bank Name": "اسم البنك", "Working Company": "الشركة",
};

export const fieldLabel = (field, isAr) => {
  if (!field) return "";
  const column = DIFF_FIELD_COLUMNS[field] || field;
  return isAr ? FIELD_LABELS_AR[column] || EXTRA_AR[column] || column : column;
};

const rowsText = (rows, isAr) => {
  const list = (rows || []).join(isAr ? "، " : ", ");
  return isAr ? `${(rows || []).length > 1 ? "الصفوف" : "الصف"} ${list}` : `${(rows || []).length > 1 ? "rows" : "row"} ${list}`;
};

// Who already holds a value, as the object of "يخص بالفعل" / "already
// belongs to". The server sends a name and code only when the importer may
// see that employee (`scope: "visible"`). Anyone else is "restricted": the
// user just learns the value is taken, support gets the details by email.
const whoText = (d, isAr) => {
  if (d?.scope === "visible") {
    const name = d.name ? q(d.name) : "";
    const code = d.clientCode ? (isAr ? ` (كود ${d.clientCode})` : ` (code ${d.clientCode})`) : "";
    return isAr ? `الموظف ${name}${code}`.replace("  ", " ") : `${name || "another employee"}${code}`;
  }
  return isAr ? "حسابًا آخر في النظام" : "another account in the system";
};

const isRestricted = (d) => d?.scope !== "visible";

// "Support has the details (ref X)", for values whose holder is hidden.
const supportNote = (d, isAr) =>
  d?.referenceId
    ? isAr
      ? ` تم إبلاغ الدعم الفني بالتفاصيل (رقم المرجع ${d.referenceId}).`
      : ` Support has been sent the details (reference ${d.referenceId}).`
    : "";

const restrictedFix = (d, isAr, what) =>
  isAr
    ? `استخدم ${what} آخر لهذا الموظف. لو متأكد أنه يخصه، تواصل مع الدعم الفني${d?.referenceId ? ` برقم المرجع ${d.referenceId}` : ""}.`
    : `Use a different ${what} for this employee. If you're sure it's theirs, contact support${d?.referenceId ? ` with reference ${d.referenceId}` : ""}.`;

// Values are wrapped in Unicode isolates so a Latin code or email keeps its
// own direction inside an Arabic sentence ("ZZ-T1", not "T1-ZZ").
const q = (v) => `«⁨${v ?? "—"}⁩»`;

const NATIONAL_ID_PROBLEMS = {
  not_digits: { ar: "يحتوي على حروف أو رموز", en: "contains letters or symbols" },
  length: { ar: "عدد أرقامه ليس 14", en: "is not 14 digits long" },
  century: { ar: "لا يبدأ بـ 2 أو 3", en: "doesn't start with 2 or 3" },
  birth_date: { ar: "تاريخ الميلاد المكتوب داخله غير صحيح", en: "has an impossible birth date inside it" },
  governorate: { ar: "كود المحافظة داخله غير معروف", en: "has an unknown governorate code" },
};

const SCOPE_PROBLEMS = {
  WORKING_COMPANY_OUT_OF_SCOPE: (p, ar) => ar
    ? `الشركة ${q(p.value || "فارغة")} ليست ضمن الشركات المسموح لك بإدارتها`
    : `Working company ${q(p.value || "empty")} is not one you're allowed to manage`,
  DEPARTMENT_REQUIRED: (p, ar) => ar
    ? `يجب تحديد القسم لشركة ${q(p.company)}`
    : `A department is required for company ${q(p.company)}`,
  DEPARTMENT_OUT_OF_SCOPE: (p, ar) => ar
    ? `القسم ${q(p.value)} في شركة ${q(p.company)} ليس ضمن صلاحياتك`
    : `Department ${q(p.value)} in ${q(p.company)} is outside your access`,
  MANAGER_REQUIRED: (p, ar) => ar
    ? "يجب كتابة كود أو اسم المدير المباشر"
    : "First Line Manager code or name is required",
};

// code -> (issue, isAr) => { title, message, fix }
const ISSUES = {
  // ── rows that are not imported ──────────────────────────────────────────
  DUPLICATE_CLIENT_CODE_IN_FILE: (i, ar) => ({
    title: ar ? "كود الموظف مكرر داخل الملف" : "Client Code repeated in the file",
    message: ar
      ? `الكود ${q(i.value)} مكتوب أيضًا في ${rowsText(i.details?.otherRows, ar)}. لا يمكن أن يتكرر نفس الكود لموظفين في نفس الشركة، لذلك تم إيقاف كل الصفوف المكررة.`
      : `Code ${q(i.value)} is also used in ${rowsText(i.details?.otherRows, ar)}. Two employees of the same client can't share a code, so every repeated row is held back.`,
    fix: ar
      ? "احذف الصف المكرر لو هو نفس الموظف، أو أعطِ كل موظف كودًا مختلفًا، ثم ارفع الملف مرة أخرى."
      : "Delete the repeated row if it's the same person, or give each employee a different code, then upload again.",
  }),
  CLIENT_NAME_MISMATCH: (i, ar) => ({
    title: ar ? "اسم العميل لا يطابق شركتك" : "Client Name doesn't match your company",
    message: ar
      ? `الصف مكتوب فيه ${q(i.value)} بينما حسابك تابع لـ ${q(i.details?.expected)}. غالبًا الملف يخص شركة أخرى أو هو ملف قديم.`
      : `The row says ${q(i.value)} but your account belongs to ${q(i.details?.expected)}. The file probably belongs to another company or is an old export.`,
    fix: ar ? "تأكد أنك رفعت الملف الصحيح، أو صحّح خانة Client Name أو اتركها فارغة." : "Check you uploaded the right file, or correct / clear the Client Name cell.",
  }),
  YEAR_ONLY_DATE: (i, ar) => ({
    title: ar ? "تاريخ مكتوب كسنة فقط" : "Date written as a year only",
    message: (i.details?.cells || [])
      .map((c) => (ar ? `${fieldLabel(c.field, ar)}: ${q(c.value)}` : `${c.field}: ${q(c.value)}`))
      .join(ar ? "، " : ", ") + (ar ? " — مكتوب السنة بدون اليوم والشهر." : " — the day and month are missing."),
    fix: ar ? "اكتب التاريخ كاملًا مثل 15/3/2015 أو 2015-03-15." : "Write the full date, e.g. 3/15/2015 or 2015-03-15.",
  }),
  MISSING_REQUIRED: (i, ar) => ({
    title: ar ? "بيانات أساسية ناقصة" : "Required data missing",
    message:
      (ar ? "ناقص: " : "Missing: ") +
      (i.missingFields || []).map((f) => fieldLabel(f, ar)).join(ar ? "، " : ", ") +
      (i.details?.invalidEmail
        ? ar
          ? `. البريد ${q(i.details.invalidEmail)} مكتوب لكنه غير صحيح فلم يُحتسب.`
          : `. The email ${q(i.details.invalidEmail)} was there but is invalid, so it didn't count.`
        : "") +
      (i.details?.invalidPhone
        ? ar
          ? `${i.details?.invalidEmail ? "" : "."} رقم الموبايل ${q(i.details.invalidPhone)} مكتوب لكنه ليس رقمًا صحيحًا فلم يُحتسب.`
          : `${i.details?.invalidEmail ? "" : "."} The phone ${q(i.details.invalidPhone)} was there but isn't a valid number, so it didn't count.`
        : "") +
      (i.details?.invalidEmail || i.details?.invalidPhone ? "" : "."),
    fix: ar
      ? "كود الموظف مطلوب دائمًا، ومعه بريد إلكتروني صحيح أو رقم موبايل على الأقل."
      : "Every row needs a Client Code plus a valid email or a phone number.",
  }),
  OUT_OF_SCOPE: (i, ar) => ({
    title: ar ? "خارج نطاق صلاحياتك" : "Outside your access",
    message: (i.details?.problems || []).length
      ? i.details.problems.map((p) => (SCOPE_PROBLEMS[p.code] || (() => p.code))(p, ar)).join(ar ? "؛ " : "; ") + "."
      : i.reason,
    fix: ar
      ? "عدّل الصف ليكون ضمن الشركات والأقسام المسموح لك بها، أو اطلب من مسؤول النظام استيراده."
      : "Change the row to a company / department you manage, or ask an admin to import it.",
  }),
  NO_DUPLICATE_DECISION: (i, ar) => ({
    title: ar ? "لم يتم اختيار إجراء للموظف الموجود" : "No decision for an existing employee",
    message: ar
      ? `الموظف بكود ${q(i.value)} موجود بالفعل في النظام ولم يتم اختيار إبقاء أو تحديث.`
      : `The employee with code ${q(i.value)} already exists and neither Keep nor Update was chosen.`,
    fix: ar ? "ارفع الملف مرة أخرى واختر إبقاء أو تحديث في خطوة مراجعة المكررات." : "Upload again and choose Keep or Update in the duplicate review step.",
  }),
  INVALID_DUPLICATE_DECISION: (i, ar) => ({
    title: ar ? "إجراء غير معروف للموظف الموجود" : "Unknown decision for an existing employee",
    message: ar ? `الإجراء المختار للكود ${q(i.value)} غير مدعوم.` : `The decision sent for code ${q(i.value)} isn't supported.`,
    fix: ar ? "ارفع الملف مرة أخرى واختر إبقاء أو تحديث." : "Upload again and choose Keep or Update.",
  }),
  EMPLOYEE_QUOTA_EXCEEDED: (i, ar) => ({
    title: ar ? "تم الوصول للحد الأقصى للموظفين" : "Employee limit reached",
    message: ar
      ? `باقة ${q(i.details?.client)} لا تسمح بإضافة موظفين أكثر، وهذا الصف كان سيتخطى الحد.`
      : `${q(i.details?.client)} can't add more employees; this row would go over the limit.`,
    fix: ar ? "اطلب من مسؤول النظام زيادة الحد، أو قلّل عدد الموظفين الجدد في الملف." : "Ask the system admin to raise the limit, or import fewer new employees.",
  }),
  CODE_GENERATION_FAILED: (i, ar) => ({
    title: ar ? "تعذّر إنشاء الكود الداخلي" : "Couldn't create the internal code",
    message: ar ? "حدثت مشكلة أثناء إنشاء الكود الداخلي لهذا الموظف." : "Something went wrong creating this employee's internal code.",
    fix: ar ? "أعد استيراد الصف، ولو تكررت المشكلة تواصل مع الدعم الفني." : "Import the row again; if it keeps failing, contact support.",
  }),
  CLIENT_CODE_RESTRICTED: (i, ar) => ({
    title: ar ? "كود الموظف مستخدم بالفعل" : "Client Code already in use",
    message: ar
      ? `الكود ${q(i.value)} مستخدم بالفعل لموظف آخر لا يمكنك إدارته، لذلك لا يمكن استخدامه لهذا الصف.${supportNote(i.details, ar)}`
      : `Code ${q(i.value)} already belongs to another employee you can't manage, so it can't be used for this row.${supportNote(i.details, ar)}`,
    fix: restrictedFix(i.details, ar, ar ? "كودًا" : "code"),
  }),
  CLIENT_CODE_TAKEN: (i, ar) => ({
    title: ar ? "الكود مستخدم لموظف آخر" : "Code already in use",
    message: ar ? `الكود ${q(i.value)} تم تسجيله لموظف آخر في نفس الشركة أثناء الاستيراد.` : `Code ${q(i.value)} was taken by another employee of the same client during the import.`,
    fix: ar ? "استخدم كودًا مختلفًا، أو حدّث بيانات الموظف الموجود بدل إضافته." : "Use a different code, or update the existing employee instead.",
  }),
  SAVE_FAILED: (i, ar) => ({
    title: ar ? "تعذّر حفظ الصف" : "Row couldn't be saved",
    message: i.reason,
    fix: ar ? "راجع بيانات الصف وأعد المحاولة، ولو تكررت المشكلة تواصل مع الدعم الفني." : "Check the row and try again; if it keeps failing, contact support.",
  }),

  // ── rows imported with a note ───────────────────────────────────────────
  INVALID_DATE: (i, ar) => ({
    title: ar ? "تاريخ غير مفهوم" : "Unreadable date",
    message: ar
      ? `${q(i.value)} في عمود ${fieldLabel(i.field, ar)} ليس تاريخًا صحيحًا، وستُحفظ الخانة فارغة.`
      : `${q(i.value)} in ${i.field} isn't a valid date; the cell will be saved empty.`,
    fix: ar ? "اكتب التاريخ مثل 15/3/2015 أو 2015-03-15." : "Write it like 3/15/2015 or 2015-03-15.",
  }),
  DATE_OUT_OF_RANGE: (i, ar) => ({
    title: ar ? "تاريخ غير منطقي" : "Unrealistic date",
    message: ar
      ? `التاريخ ${q(i.value)} في عمود ${fieldLabel(i.field, ar)} خارج النطاق المعقول (1900–2100)، وستُحفظ الخانة فارغة.`
      : `${q(i.value)} in ${i.field} is outside 1900–2100; the cell will be saved empty.`,
    fix: ar ? "راجع السنة المكتوبة." : "Check the year.",
  }),
  INVALID_NUMBER: (i, ar) => ({
    title: ar ? "قيمة ليست رقمًا" : "Not a number",
    message: ar
      ? `${q(i.value)} في عمود ${fieldLabel(i.field, ar)} ليست رقمًا، وستُحفظ الخانة فارغة.`
      : `${q(i.value)} in ${i.field} isn't a number; the cell will be saved empty.`,
    fix: ar ? "اكتب أرقامًا فقط بدون حروف أو رموز عملة." : "Use digits only, no letters or currency symbols.",
  }),
  INVALID_INTEGER: (i, ar) => ({
    title: ar ? "مطلوب رقم صحيح" : "Whole number expected",
    message: ar
      ? `${q(i.value)} في عمود ${fieldLabel(i.field, ar)} ليست رقمًا صحيحًا، وستُحفظ الخانة فارغة.`
      : `${q(i.value)} in ${i.field} isn't a whole number; the cell will be saved empty.`,
    fix: ar ? "اكتب رقمًا صحيحًا بدون كسور." : "Write a whole number without decimals.",
  }),
  INVALID_BOOLEAN: (i, ar) => ({
    title: ar ? "قيمة نعم / لا غير مفهومة" : "Yes / No value not recognized",
    message: ar
      ? `${q(i.value)} في عمود ${fieldLabel(i.field, ar)} غير مفهومة، وستُحفظ الخانة فارغة.`
      : `${q(i.value)} in ${i.field} isn't recognized; the cell will be saved empty.`,
    fix: ar ? "اكتب Yes أو No (أو 1 / 0)." : "Write Yes or No (or 1 / 0).",
  }),
  EMAIL_INVALID: (i, ar) => ({
    title: ar ? "البريد الإلكتروني غير صحيح" : "Invalid email",
    message: ar ? `${q(i.value)} ليس بريدًا إلكترونيًا صحيحًا، ولن يتم حفظه.` : `${q(i.value)} isn't a valid email address and won't be saved.`,
    fix: ar ? "صحّح البريد بصيغة name@company.com (بدون مسافات)." : "Use the form name@company.com, without spaces.",
  }),
  EMAIL_DUPLICATE_IN_FILE: (i, ar) => ({
    title: ar ? "البريد مكرر داخل الملف" : "Email repeated in the file",
    message: ar
      ? `نفس البريد ${q(i.value)} مكتوب أيضًا في ${rowsText(i.details?.otherRows, ar)}. البريد لا يتكرر نهائيًا، لذلك تم إيقاف كل الصفوف التي تحمله.`
      : `The same email ${q(i.value)} is also in ${rowsText(i.details?.otherRows, ar)}. An email can never be repeated, so every row carrying it is held back.`,
    fix: ar ? "كل موظف يحتاج بريدًا مختلفًا، صحّح البريد ثم ارفع الملف مرة أخرى." : "Each employee needs their own email; fix it and upload the file again.",
  }),
  EMAIL_TAKEN: (i, ar) => ({
    title: ar ? "البريد مستخدم بالفعل" : "Email already in use",
    message: ar
      ? `البريد ${q(i.value)} يخص بالفعل ${whoText(i.details, ar)}، والبريد لا يتكرر نهائيًا، لذلك لن يتم استيراد هذا الصف.${supportNote(i.details, ar)}`
      : `${q(i.value)} already belongs to ${whoText(i.details, ar)}. An email can never be repeated, so this row isn't imported.${supportNote(i.details, ar)}`,
    fix: isRestricted(i.details)
      ? restrictedFix(i.details, ar, ar ? "بريدًا" : "email")
      : ar ? "استخدم بريدًا آخر لهذا الموظف، أو تأكد أنه ليس نفس الشخص." : "Use a different email, or check it isn't the same person.",
  }),
  PHONE_LEADING_ZERO_LOST: (i, ar) => ({
    title: ar ? "الصفر الأول في رقم الموبايل اختفى" : "Phone number lost its leading 0",
    message: ar
      ? `الرقم مكتوب ${q(i.value)}، والغالب أن الإكسل حذف الصفر من أوله (الصحيح ${q(i.details?.suggested)}).`
      : `The number reads ${q(i.value)}; Excel most likely dropped the leading 0 (should be ${q(i.details?.suggested)}).`,
    fix: ar ? "اجعل عمود Phone number من نوع Text في الإكسل واكتب الرقم كاملًا." : "Format the Phone number column as Text in Excel and type the full number.",
  }),
  PHONE_INVALID: (i, ar) => ({
    title: ar ? "رقم الموبايل غير صحيح" : "Invalid phone number",
    message: ar
      ? `${q(i.value)} ليس رقم موبايل صحيحًا، ولن يتم حفظه.`
      : `${q(i.value)} isn't a valid phone number and won't be saved.`,
    fix: ar ? "اكتب الرقم كاملًا من 8 إلى 15 رقمًا، مثل 01012345678." : "Write the full number, 8 to 15 digits, e.g. 01012345678.",
  }),
  PHONE_DUPLICATE_IN_FILE: (i, ar) => ({
    title: ar ? "رقم الموبايل مكرر داخل الملف" : "Phone repeated in the file",
    message: ar
      ? `نفس الرقم ${q(i.value)} مكتوب أيضًا في ${rowsText(i.details?.otherRows, ar)} (حتى لو بشكل مختلف مثل بدون الصفر أو بـ ‎+20). رقم الموبايل لا يتكرر نهائيًا، لذلك تم إيقاف كل الصفوف التي تحمله.`
      : `The same number ${q(i.value)} is also in ${rowsText(i.details?.otherRows, ar)} (even if written differently, e.g. without the 0 or with +20). A phone number can never be repeated, so every row carrying it is held back.`,
    fix: ar ? "كل موظف يحتاج رقمًا مختلفًا، صحّح الرقم ثم ارفع الملف مرة أخرى." : "Each employee needs their own number; fix it and upload the file again.",
  }),
  PHONE_TAKEN: (i, ar) => ({
    title: ar ? "رقم الموبايل مستخدم بالفعل" : "Phone number already in use",
    message: ar
      ? `الرقم ${q(i.value)} يخص بالفعل ${whoText(i.details, ar)}، ورقم الموبايل لا يتكرر نهائيًا، لذلك لن يتم استيراد هذا الصف.${supportNote(i.details, ar)}`
      : `${q(i.value)} already belongs to ${whoText(i.details, ar)}. A phone number can never be repeated, so this row isn't imported.${supportNote(i.details, ar)}`,
    fix: isRestricted(i.details)
      ? restrictedFix(i.details, ar, ar ? "رقمًا" : "number")
      : ar ? "استخدم رقمًا آخر، أو تأكد أنه ليس نفس الشخص." : "Use another number, or check it isn't the same person.",
  }),
  NATIONAL_ID_INVALID: (i, ar) => {
    const digits = String(i.value || "").replace(/\D/g, "");
    const problem = NATIONAL_ID_PROBLEMS[i.details?.problem];
    const why = problem ? problem[ar ? "ar" : "en"] : ar ? "غير صحيح" : "is not valid";
    const n = digits.length;
    const arDigits = n >= 3 && n <= 10 ? `${n} أرقام` : `${n} رقمًا`;
    const lengthNote = i.details?.problem === "length" ? (ar ? ` (${arDigits})` : ` (${n} digits)`) : "";
    return {
      title: ar ? "الرقم القومي غير صحيح" : "Invalid National ID",
      message: ar
        ? `الرقم ${q(i.value)} ${why}${lengthNote}. الرقم القومي المصري 14 رقمًا.`
        : `${q(i.value)} ${why}${lengthNote}. An Egyptian National ID is 14 digits.`,
      fix: ar
        ? "راجع الرقم من البطاقة. لو ظهر بشكل 2.9E+13 اجعل العمود من نوع Text."
        : "Check it against the ID card. If it shows as 2.9E+13, format the column as Text.",
    };
  },
  NATIONAL_ID_DUPLICATE_IN_FILE: (i, ar) => ({
    title: ar ? "الرقم القومي مكرر داخل الملف" : "National ID repeated in the file",
    message: ar
      ? `نفس الرقم القومي ${q(i.value)} مكتوب أيضًا في ${rowsText(i.details?.otherRows, ar)} — غالبًا نفس الشخص مكتوب مرتين. الرقم القومي لا يتكرر نهائيًا، لذلك تم إيقاف كل الصفوف التي تحمله.`
      : `The same National ID ${q(i.value)} is also in ${rowsText(i.details?.otherRows, ar)} — most likely the same person twice. A National ID can never be repeated, so every row carrying it is held back.`,
    fix: ar ? "احذف الصف المكرر، أو صحّح الرقم لو هما شخصان مختلفان، ثم ارفع الملف مرة أخرى." : "Delete the repeated row, or fix the number if they're different people, then upload again.",
  }),
  NATIONAL_ID_TAKEN: (i, ar) => ({
    title: ar ? "الرقم القومي مسجل لموظف آخر" : "National ID belongs to another employee",
    message: ar
      ? `الرقم القومي يخص بالفعل ${whoText(i.details, ar)} — غالبًا نفس الشخص بكود مختلف. الرقم القومي لا يتكرر نهائيًا، لذلك لن يتم استيراد هذا الصف.${supportNote(i.details, ar)}`
      : `This National ID already belongs to ${whoText(i.details, ar)} — probably the same person under another code. A National ID can never be repeated, so this row isn't imported.${supportNote(i.details, ar)}`,
    fix: isRestricted(i.details)
      ? restrictedFix(i.details, ar, ar ? "رقمًا قوميًا" : "National ID")
      : ar
        ? "لو هو نفس الموظف، استخدم نفس الكود لتحديث بياناته بدل إضافته من جديد."
        : "If it's the same person, use their existing code to update them instead of adding a new employee.",
  }),
  ACCOUNT_QUOTA_EXCEEDED: (i, ar) => ({
    title: ar ? "لن يتم إنشاء حساب دخول" : "No login account will be created",
    message: ar
      ? `تم الوصول للحد الأقصى لحسابات ${q(i.details?.client)}، فسيُضاف الموظف بدون حساب دخول.`
      : `${q(i.details?.client)} has reached its account limit; the employee is added without a login.`,
    fix: ar ? "اطلب زيادة حد الحسابات، ثم أنشئ الحساب لاحقًا." : "Ask for a higher account limit, then create the login later.",
  }),
  ACCOUNT_NOT_CREATED: (i, ar) => ({
    title: ar ? "لم يتم إنشاء حساب دخول" : "No login account created",
    message: i.details?.via === "phone"
      ? ar
        ? `رقم الموبايل ${q(i.value)} هو بالفعل رقم دخول حساب آخر، فتم حفظ الموظف بدون حساب دخول.`
        : `${q(i.value)} is already another account's login phone, so the employee was saved without a login.`
      : ar
        ? `البريد ${q(i.value)} هو بالفعل بريد دخول حساب آخر، فتم حفظ الموظف بدون حساب دخول.`
        : `${q(i.value)} is already another account's login email, so the employee was saved without a login.`,
    fix: ar
      ? "تأكد أنه ليس نفس الشخص، ثم أنشئ له حسابًا ببريد أو رقم مختلف من صفحة الموظف."
      : "Check it isn't the same person, then create a login with a different email or phone from the employee page.",
  }),
  LOGIN_EMAIL_NOT_UPDATED: (i, ar) => ({
    title: ar ? "لم يتم تغيير بريد الدخول" : "Login email not changed",
    message: ar
      ? `البريد الجديد ${q(i.value)} مستخدم لحساب آخر، فبقي بريد الدخول ${q(i.details?.current)}.`
      : `The new email ${q(i.value)} belongs to another account, so the login stays ${q(i.details?.current)}.`,
    fix: ar ? "استخدم بريدًا غير مستخدم لهذا الموظف." : "Use an email no other account uses.",
  }),
  WELCOME_EMAIL_FAILED: (i, ar) => ({
    title: ar ? "لم يُرسل بريد الترحيب" : "Welcome email not sent",
    message: ar
      ? `تم إنشاء حساب الدخول، لكن تعذّر إرسال بيانات الدخول إلى ${q(i.value)}.`
      : `The login account was created, but the login details couldn't be sent to ${q(i.value)}.`,
    fix: ar
      ? "تأكد من صحة البريد، ثم أرسل بيانات الدخول للموظف يدويًا أو أعد تعيين كلمة المرور."
      : "Check the address, then share the login details manually or reset the password.",
  }),
};

// ── Whole-import problems ──────────────────────────────────────────────────
// Errors that stop the import before any row is read (file, template, access,
// connection), keyed by the server's error code, or by HTTP status when the
// server sent none.
const REQUEST_ERRORS = {
  IMPORT_NO_FILE: (b, ar) => ({
    title: ar ? "لم يتم اختيار ملف" : "No file selected",
    message: ar ? "لم يصل أي ملف إلى الخادم." : "No file reached the server.",
    fix: ar ? "اختر ملف الإكسل مرة أخرى." : "Choose the Excel file again.",
  }),
  IMPORT_FILE_TYPE: (b, ar) => ({
    title: ar ? "نوع الملف غير مدعوم" : "Unsupported file type",
    message: ar
      ? `الملف ${q(b.details?.fileName)} ليس ملف إكسل.`
      : `${q(b.details?.fileName)} is not an Excel file.`,
    fix: ar ? "ارفع قالب الموظفين بصيغة xlsx أو xls أو csv." : "Upload the employees template as .xlsx, .xls or .csv.",
  }),
  IMPORT_FILE_UNREADABLE: (b, ar) => ({
    title: ar ? "تعذّرت قراءة الملف" : "The file couldn't be read",
    message: ar
      ? "قد يكون الملف تالفًا أو محميًا بكلمة مرور أو ليس ملف إكسل فعلًا (مثلًا تم تغيير امتداده فقط)."
      : "It may be damaged, password-protected, or not really an Excel file (e.g. only its extension was renamed).",
    fix: ar ? "افتح الملف في الإكسل، أزل كلمة المرور إن وجدت، واحفظه بصيغة xlsx ثم ارفعه." : "Open it in Excel, remove any password, save as .xlsx and upload again.",
  }),
  IMPORT_FILE_EMPTY: (b, ar) => ({
    title: ar ? "الملف لا يحتوي على موظفين" : "No employees in the file",
    message: ar
      ? `الورقة الأولى${b.details?.sheetName ? ` ${q(b.details.sheetName)}` : ""} لا تحتوي على أي صفوف تحت صف العناوين.${b.details?.sheetCount > 1 ? " الاستيراد يقرأ الورقة الأولى فقط." : ""}`
      : `The first sheet${b.details?.sheetName ? ` ${q(b.details.sheetName)}` : ""} has no rows under the header row.${b.details?.sheetCount > 1 ? " Only the first sheet is imported." : ""}`,
    fix: ar
      ? "اكتب بيانات الموظفين تحت صف العناوين في الورقة الأولى، أو انقل ورقة الموظفين لتكون الأولى."
      : "Fill employees under the header row of the first sheet, or move the employees sheet to be first.",
  }),
  IMPORT_TEMPLATE_MISMATCH: (b, ar) => ({
    title: ar ? "الملف لا يطابق قالب الموظفين" : "The file doesn't match the template",
    message: ar
      ? "صف العناوين (الصف الأول) لا يحتوي على الأعمدة الأساسية بالأسماء المطلوبة بالضبط."
      : "The header row (row 1) doesn't contain the required columns with their exact names.",
    fix: ar
      ? "حمّل قالب الاستيراد من الصفحة وانقل بياناتك إليه، أو صحّح أسماء الأعمدة كما هو موضح."
      : "Download the import template and paste your data into it, or rename the columns as shown.",
    missingColumns: b.details?.missingColumns || [],
    foundColumns: b.details?.foundColumns || [],
  }),
  IMPORT_CLIENT_UNRESOLVED: (b, ar) => ({
    title: ar ? "حسابك غير مربوط بشركة" : "Your account has no company",
    message: ar ? "لا يمكن الاستيراد لأن حسابك غير مرتبط بأي شركة." : "Employees can't be imported because your account isn't linked to a company.",
    fix: ar ? "تواصل مع مسؤول النظام لربط حسابك بالشركة." : "Ask an administrator to link your account to a company.",
  }),
  IMPORT_CLIENT_FORBIDDEN: (b, ar) => ({
    title: ar ? "لا يمكنك الاستيراد لشركة أخرى" : "Can't import for another company",
    message: ar
      ? `الطلب كان لشركة ${q(b.details?.requested)} بينما حسابك تابع لـ ${q(b.details?.yours)}.`
      : `The request targeted ${q(b.details?.requested)}, but your account belongs to ${q(b.details?.yours)}.`,
    fix: ar ? "أعد تحميل الصفحة ثم استورد مرة أخرى." : "Reload the page and import again.",
  }),
  IMPORT_BAD_REQUEST: (b, ar) => ({
    title: ar ? "بيانات الاستيراد غير صالحة" : "Invalid import request",
    message: (ar ? b.messages?.ar : b.messages?.en) || b.message,
    fix: ar ? "أعد تحميل الصفحة ثم استورد مرة أخرى." : "Reload the page and import again.",
  }),
};

const STATUS_ERRORS = {
  network: (b, ar) => ({
    title: ar ? "تعذّر الاتصال بالخادم" : "Couldn't reach the server",
    message: ar ? "الطلب لم يصل للخادم، غالبًا بسبب انقطاع الإنترنت أو توقف الخادم." : "The request never reached the server, usually a lost connection or the server being down.",
    fix: ar ? "تأكد من اتصالك بالإنترنت ثم حاول مرة أخرى. لم يتم حفظ أي شيء." : "Check your connection and try again. Nothing was saved.",
  }),
  401: (b, ar) => ({
    title: ar ? "انتهت الجلسة" : "Session expired",
    message: ar ? "تم تسجيل خروجك قبل اكتمال الاستيراد." : "You were signed out before the import finished.",
    fix: ar ? "سجّل الدخول مرة أخرى ثم أعد الاستيراد." : "Sign in again and repeat the import.",
  }),
  403: (b, ar) => ({
    title: ar ? "ليس لديك صلاحية الاستيراد" : "You can't import employees",
    message: (ar ? b.messages?.ar : b.messages?.en) || (ar ? "صلاحياتك لا تسمح باستيراد الموظفين." : "Your role doesn't allow importing employees."),
    fix: ar ? "اطلب من مسؤول النظام منحك صلاحية استيراد الموظفين." : "Ask an administrator for the employee import permission.",
  }),
  413: (b, ar) => ({
    title: ar ? "الملف كبير جدًا" : "File too large",
    message: ar ? "حجم الملف أكبر من المسموح به للرفع." : "The file is larger than the upload limit.",
    fix: ar ? "قسّم الموظفين على أكثر من ملف، أو احذف الأوراق والصور غير المطلوبة." : "Split the employees into smaller files, or remove extra sheets and images.",
  }),
  server: (b, ar) => ({
    title: ar ? "حدث خطأ في الخادم" : "Server error",
    message:
      ((ar ? b.messages?.ar : b.messages?.en) || b.message || (ar ? "حدث خطأ غير متوقع أثناء الاستيراد." : "Something unexpected went wrong during the import.")) +
      (b.referenceId ? (ar ? ` (رقم المرجع: ${b.referenceId})` : ` (reference: ${b.referenceId})`) : ""),
    fix: ar
      ? "حاول مرة أخرى بعد قليل. لو تكرر الخطأ أرسل رقم المرجع للدعم الفني."
      : "Try again shortly. If it keeps happening, send the reference number to support.",
  }),
};

/** Thrown by the import requests so the screen can explain the failure. */
export class ImportRequestError extends Error {
  constructor({ status = null, body = {}, network = false } = {}) {
    super(body?.message || body?.error || (network ? "Network error" : `HTTP ${status}`));
    this.name = "ImportRequestError";
    this.status = status;
    this.body = body || {};
    this.network = network;
  }
}

/** POST the import form; resolves with the JSON body or throws ImportRequestError. */
export const postImportRequest = async (fetcher, url, formData) => {
  let response;
  try {
    response = await fetcher(url, { method: "POST", body: formData });
  } catch {
    throw new ImportRequestError({ network: true });
  }
  // A proxy (e.g. a 413 from nginx) may answer with HTML, not JSON.
  const body = await response.json().catch(() => ({}));
  if (!response.ok || !body?.success) throw new ImportRequestError({ status: response.status, body });
  return body;
};

/** { title, message, fix, missingColumns?, foundColumns? } for a failed import request. */
export const explainImportRequestError = (err, isAr) => {
  const body = err?.body || {};
  if (err?.network) return STATUS_ERRORS.network(body, isAr);
  if (body.code && REQUEST_ERRORS[body.code]) return REQUEST_ERRORS[body.code](body, isAr);
  if (err?.status === 401 || err?.status === 403 || err?.status === 413) return STATUS_ERRORS[err.status](body, isAr);
  if (!(err instanceof ImportRequestError)) {
    return {
      title: isAr ? "تعذّر إكمال الاستيراد" : "The import couldn't finish",
      message: err?.message || (isAr ? "خطأ غير معروف" : "Unknown error"),
      fix: isAr ? "حاول مرة أخرى." : "Please try again.",
    };
  }
  return STATUS_ERRORS.server(body, isAr);
};

// Problems after the rows were saved (not tied to a row).
const NOTICES = {
  MANAGER_LINKING_FAILED: (ar) => ({
    title: ar ? "لم يتم ربط المديرين" : "Managers weren't linked",
    message: ar
      ? "تم حفظ الموظفين، لكن حدث خطأ أثناء ربط كل موظف بمديره المباشر."
      : "The employees were saved, but linking each one to their First Line Manager failed.",
    fix: ar
      ? "استورد نفس الملف مرة أخرى واختر «تحديث» لربطهم، أو حدّد المدير من تعديل الموظف."
      : "Import the same file again choosing Update to link them, or set the manager from Edit Employee.",
  }),
  GROUP_SYNC_FAILED: (ar) => ({
    title: ar ? "لم يتم تحديث مجموعات الدردشة" : "Chat groups weren't updated",
    message: ar
      ? "تم حفظ الموظفين، لكن لم تتم إضافتهم تلقائيًا لمجموعات الدردشة المرتبطة."
      : "The employees were saved, but they weren't added to the automatic chat groups.",
    fix: ar ? "ستُحدّث المجموعات مع أي تعديل قادم، أو تواصل مع الدعم الفني." : "Groups catch up on the next change; contact support if they don't.",
  }),
};

export const explainNotice = (notice, isAr) =>
  NOTICES[notice?.code]?.(isAr) || { title: isAr ? "ملاحظة" : "Note", message: notice?.message || "", fix: null };

// Older servers send no code: infer it from the payload's shape.
const inferFailedCode = (f) => {
  if (f.code) return f.code;
  if (Array.isArray(f.missingFields) && f.missingFields.length) return "MISSING_REQUIRED";
  if (/^Duplicate Client Code/i.test(f.reason || "")) return "DUPLICATE_CLIENT_CODE_IN_FILE";
  return null;
};

const explain = (code, issue, isAr) => {
  const build = ISSUES[code];
  if (build) return build(issue, isAr);
  // Unknown code: still show the row, with the server's own words.
  return {
    title: isAr ? "مشكلة في الصف" : "Problem in this row",
    message:
      issue.reason ||
      [issue.field && fieldLabel(issue.field, isAr), issue.value && q(issue.value), issue.expectedType]
        .filter(Boolean)
        .join(" — ") ||
      (isAr ? "خطأ غير معروف" : "Unknown error"),
    fix: null,
  };
};

/**
 * Merge failed rows and warnings into one list per spreadsheet row:
 *   [{ row, employee, blocked, issues: [{ code, severity, title, message, fix, field, value }] }]
 * sorted by row. `blocked` rows are not imported at all.
 */
export const buildRowIssues = ({ failed = [], parseWarnings = [] }, isAr) => {
  const byRow = new Map();
  const entry = (row, employee) => {
    if (!byRow.has(row)) byRow.set(row, { row, employee: employee || null, blocked: false, issues: [] });
    const e = byRow.get(row);
    if (!e.employee && employee) e.employee = employee;
    return e;
  };

  for (const f of failed) {
    const e = entry(f.row, f.employee);
    const code = inferFailedCode(f);
    e.blocked = true;
    e.issues.push({ code: code || "UNKNOWN", severity: "error", field: f.field, value: f.value, ...explain(code, f, isAr) });
  }
  for (const w of parseWarnings) {
    const e = entry(w.row, w.employee);
    for (const p of w.parseErrors || []) {
      e.issues.push({ code: p.code || "UNKNOWN", severity: "warning", field: p.field, value: p.value, ...explain(p.code, p, isAr) });
    }
  }
  return [...byRow.values()].sort((a, b) => a.row - b.row);
};

// One-line reason for a duplicate-review row: which employee the code matches.
export const describeDuplicate = (dup, isAr) => {
  const who = dup?.existing?.sr ? q(dup.existing.sr) : isAr ? "موظف" : "an employee";
  const code = dup?.existing?.clientCode ?? dup?.incoming?.clientCode ?? "—";
  return isAr
    ? `الكود ${q(code)} مسجل بالفعل للموظف ${who} في النظام، لذلك هذا الصف يعتبر تحديثًا لبياناته وليس موظفًا جديدًا.`
    : `Code ${q(code)} already belongs to ${who} in the system, so this row updates that employee rather than adding a new one.`;
};

// CSV (UTF-8 with BOM so Excel shows Arabic correctly) of every issue.
export const downloadIssuesReport = (rows, isAr, fileName) => {
  const head = isAr
    ? ["الصف", "الموظف", "كود الموظف", "الحالة", "المشكلة", "التفاصيل", "طريقة الحل"]
    : ["Row", "Employee", "Client Code", "Status", "Problem", "Details", "How to fix"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [head.map(esc).join(",")];
  for (const r of rows) {
    for (const issue of r.issues) {
      lines.push(
        [
          r.row,
          r.employee?.name,
          r.employee?.clientCode,
          r.blocked ? (isAr ? "لن يتم استيراده" : "Not imported") : isAr ? "يُستورد مع ملاحظة" : "Imported with a note",
          issue.title,
          issue.message,
          issue.fix,
        ].map(esc).join(","),
      );
    }
  }
  const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
